import { useState, useEffect, useRef } from "react";
import { X, Tag, Sparkles } from "lucide-react";
import supabase from "../../lib/supabase";
import { useApp } from "../../context/AppContext";

const ROTATE_INTERVAL = 8000; // ms between banner shuffles

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function buildDateLabel(show_from, show_until) {
  const from = formatDate(show_from);
  const until = formatDate(show_until);
  if (from && until) return `From ${from} until ${until}`;
  if (from) return `From ${from}`;
  if (until) return `Until ${until}`;
  return null;
}

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isInTimeframe(show_from, show_until) {
  const today = todayISO();
  if (show_from && today < show_from) return false;
  if (show_until && today > show_until) return false;
  return true;
}

function filterActiveBanners(rows) {
  return rows.filter((row) => {
    if (row.validity_type === "permanent") return true;
    if (row.validity_type === "timeframe")
      return isInTimeframe(row.show_from, row.show_until);
    return false;
  });
}

export default function PromoBanner() {
  const { mobileMenuOpen } = useApp();
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true); // drives fade transition
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    supabase
      .from("promo_banner")
      .select(
        "id, enabled, coupon_code, message, validity_type, show_from, show_until",
      )
      .eq("enabled", true)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data?.length) {
          const active = filterActiveBanners(data);
          if (active.length) setBanners(active);
        }
      });
  }, []);

  // Rotate through banners every 8 s with a smooth fade
  useEffect(() => {
    if (banners.length <= 1 || dismissed) return;

    timerRef.current = setInterval(() => {
      // fade out
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % banners.length);
        setCopied(false);
        // fade in
        setVisible(true);
      }, 400);
    }, ROTATE_INTERVAL);

    return () => clearInterval(timerRef.current);
  }, [banners, dismissed]);

  if (!banners.length || dismissed || mobileMenuOpen) return null;

  const banner = banners[index];
  const dateLabel =
    banner.validity_type === "timeframe"
      ? buildDateLabel(banner.show_from, banner.show_until)
      : null;

  const handleCopy = () => {
    navigator.clipboard.writeText(banner.coupon_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="relative w-full flex items-center justify-center px-10 py-2.5"
      style={{
        backgroundColor: "#b81d51",
        color: "#ffffff",
        minHeight: "38px",
      }}
    >
      {/* Content fades between banners */}
      <div
        className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-center transition-opacity duration-400"
        style={{ opacity: visible ? 1 : 0 }}
      >
        {banner.coupon_code ? (
          <Tag className="w-3.5 h-3.5 shrink-0 opacity-80" />
        ) : (
          <Sparkles className="w-3.5 h-3.5 shrink-0 opacity-80" />
        )}

        <span className="font-body text-xs font-medium leading-snug">
          {banner.message}
        </span>

        {dateLabel && (
          <span className="font-body text-xs opacity-75 leading-snug">
            · {dateLabel}
          </span>
        )}

        {banner.coupon_code && (
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 border border-white/40 text-white font-body text-[11px] font-bold tracking-widest uppercase px-2.5 py-0.5 rounded-full transition-all"
            title="Copy coupon code"
          >
            {banner.coupon_code}
            <span className="text-[10px] font-normal opacity-75 normal-case tracking-normal">
              {copied ? "Copied!" : "Copy"}
            </span>
          </button>
        )}

        {/* Dots indicator — only when multiple banners */}
        {banners.length > 1 && (
          <div className="flex items-center gap-1 ml-1">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setVisible(false);
                  setTimeout(() => {
                    setIndex(i);
                    setCopied(false);
                    setVisible(true);
                  }, 400);
                }}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === index ? "16px" : "5px",
                  height: "5px",
                  backgroundColor:
                    i === index
                      ? "rgba(255,255,255,0.95)"
                      : "rgba(255,255,255,0.4)",
                }}
                aria-label={`Banner ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss banner"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
